import React, { useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Insights as InsightsIcon,
  Link as LinkIcon,
  Refresh as RefreshIcon,
  Stars as StarsIcon,
  GroupAdd as GroupAddIcon,
  Campaign as CampaignIcon,
} from '@mui/icons-material';
import { useGetReferralAnalyticsOverviewQuery } from '../../store/api/referralAnalyticsApi';

const StatCard = ({ title, value, subtitle, icon: Icon, color = 'primary' }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4" fontWeight="bold" color={`${color}.main`}>
            {value}
          </Typography>
          {subtitle ? (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          ) : null}
        </Box>
        <Box
          sx={{
            backgroundColor: `${color}.light`,
            borderRadius: 2,
            p: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon sx={{ color: `${color}.main` }} />
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

const percentOf = (value, total) => {
  if (!total) return 0;
  return Number(((value / total) * 100).toFixed(1));
};

const maskPhone = (phone) => {
  if (!phone) return '-';
  return phone.length <= 4 ? phone : `***${phone.slice(-6)}`;
};

const ReferralAnalytics = () => {
  const [days, setDays] = useState(14);
  const { data, isLoading, refetch, isFetching } = useGetReferralAnalyticsOverviewQuery(days);

  const analytics = data?.data || {};
  const summary = analytics.summary || {};
  const trackedSignups = summary.trackedSignups || 0;
  const channelBreakdown = analytics.channelBreakdown || [];
  const topPlayerReferrers = analytics.topPlayerReferrers || [];
  const topAffiliateCodes = analytics.topAffiliateCodes || [];
  const recentTrackedSignups = analytics.recentTrackedSignups || [];
  const trend = analytics.trend;

  const trendTotals = useMemo(
    () =>
      (trend || []).reduce(
        (accumulator, row) => ({
          totalTracked: accumulator.totalTracked + (row.totalTracked || 0),
          rewardedReferrals: accumulator.rewardedReferrals + (row.rewardedReferrals || 0),
          estimatedPointsEarned:
            accumulator.estimatedPointsEarned + (row.estimatedPointsEarned || 0),
        }),
        { totalTracked: 0, rewardedReferrals: 0, estimatedPointsEarned: 0 },
      ),
    [trend],
  );

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Referral Analytics
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Super admin view of how players share the game, which channels drive signups,
            and how many referral points have been earned.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1.5} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Window</InputLabel>
            <Select
              value={days}
              label="Window"
              onChange={(event) => setDays(Number(event.target.value))}
            >
              <MenuItem value={7}>7 days</MenuItem>
              <MenuItem value={14}>14 days</MenuItem>
              <MenuItem value={30}>30 days</MenuItem>
              <MenuItem value={60}>60 days</MenuItem>
            </Select>
          </FormControl>

          <Tooltip title="Refresh Data">
            <IconButton onClick={refetch} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {(isLoading || isFetching) ? <LinearProgress sx={{ mb: 2 }} /> : null}

      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Tracked Signups"
            value={trackedSignups.toLocaleString()}
            subtitle={`${percentOf(trackedSignups, summary.totalUsers || 0)}% of all users`}
            icon={GroupAddIcon}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Player Referrals"
            value={(summary.directReferrals || 0).toLocaleString()}
            subtitle={`${summary.rewardedReferrals || 0} rewarded, ${summary.pendingRewardedSignups || 0} pending`}
            icon={LinkIcon}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Affiliate Code Signups"
            value={(summary.affiliateSignups || 0).toLocaleString()}
            subtitle={`${summary.bothTracked || 0} also had a player referrer`}
            icon={CampaignIcon}
            color="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Referral Points Earned"
            value={(summary.totalReferralPointsEarned || 0).toLocaleString()}
            subtitle={`${summary.referralBonusPointsPerReward || 0} pts per reward`}
            icon={StarsIcon}
            color="success"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Stack direction="row" spacing={1} alignItems="center" mb={2}>
              <InsightsIcon color="primary" />
              <Typography variant="h6">How People Are Sharing</Typography>
            </Stack>

            <Stack spacing={2}>
              {channelBreakdown.map((channel) => {
                const percent = percentOf(channel.value || 0, summary.totalUsers || 0);

                return (
                  <Box key={channel.id}>
                    <Stack direction="row" justifyContent="space-between" mb={0.5}>
                      <Typography variant="body2">{channel.label}</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {(channel.value || 0).toLocaleString()} ({percent}%)
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(percent, 100)}
                      sx={{ height: 8, borderRadius: 1.5 }}
                    />
                  </Box>
                );
              })}
            </Stack>

            <Stack spacing={1.25} mt={3}>
              <Typography variant="body2" color="text.secondary">
                Active referrers
              </Typography>
              <Typography variant="h5" fontWeight="bold" color="primary.main">
                {(summary.activeReferrers || 0).toLocaleString()}
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Average points per rewarded signup
              </Typography>
              <Typography variant="h6" fontWeight="bold" color="success.main">
                {summary.avgPointsPerRewardedReferral || 0}
              </Typography>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Referral Trend ({days} days)
            </Typography>

            <Grid container spacing={2} mb={2}>
              <Grid item xs={12} sm={4}>
                <Chip
                  label={`${trendTotals.totalTracked.toLocaleString()} tracked signups`}
                  color="primary"
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Chip
                  label={`${trendTotals.rewardedReferrals.toLocaleString()} rewarded`}
                  color="info"
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Chip
                  label={`${trendTotals.estimatedPointsEarned.toLocaleString()} est. points`}
                  color="success"
                  variant="outlined"
                />
              </Grid>
            </Grid>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Day</TableCell>
                    <TableCell align="right">Tracked</TableCell>
                    <TableCell align="right">Player</TableCell>
                    <TableCell align="right">Affiliate</TableCell>
                    <TableCell align="right">Rewarded</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(trend || []).map((row) => (
                    <TableRow key={row.dateKey}>
                      <TableCell>{row.label}</TableCell>
                      <TableCell align="right">{row.totalTracked}</TableCell>
                      <TableCell align="right">{row.directReferrals}</TableCell>
                      <TableCell align="right">{row.affiliateSignups}</TableCell>
                      <TableCell align="right">{row.rewardedReferrals}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Top Player Referrers
            </Typography>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell align="right">Referrals</TableCell>
                    <TableCell align="right">Points Earned</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topPlayerReferrers.length ? (
                    topPlayerReferrers.map((user, index) => (
                      <TableRow key={user._id}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                          <Stack spacing={0.25}>
                            <Typography variant="body2" fontWeight="medium">
                              {user.username || 'Unknown user'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {maskPhone(user.phone)}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell align="right">
                          <Chip label={user.referralCount || 0} size="small" color="primary" />
                        </TableCell>
                        <TableCell align="right">
                          {(user.referralBonusPointsEarned || 0).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        No rewarded player referrals yet.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Top Affiliate Codes
            </Typography>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Code</TableCell>
                    <TableCell align="right">Signups</TableCell>
                    <TableCell align="right">Rewarded</TableCell>
                    <TableCell align="right">Reward Rate</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topAffiliateCodes.length ? (
                    topAffiliateCodes.map((row) => (
                      <TableRow key={row.referralCode}>
                        <TableCell>
                          <Chip label={row.referralCode} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell align="right">{row.signups || 0}</TableCell>
                        <TableCell align="right">{row.rewardedReferrals || 0}</TableCell>
                        <TableCell align="right">{row.rewardRate || 0}%</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        No affiliate-coded signups found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Recent Tracked Signups
        </Typography>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Player</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Player Referrer</TableCell>
                <TableCell>Affiliate Code</TableCell>
                <TableCell>Reward Status</TableCell>
                <TableCell>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recentTrackedSignups.length ? (
                recentTrackedSignups.map((user) => (
                  <TableRow key={user._id}>
                    <TableCell>{user.username || '-'}</TableCell>
                    <TableCell>{maskPhone(user.phone)}</TableCell>
                    <TableCell>{user.referredBy ? maskPhone(user.referredBy) : '-'}</TableCell>
                    <TableCell>{user.referralCode || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={user.referralRewardIssued ? 'Rewarded' : 'Waiting'}
                        size="small"
                        color={user.referralRewardIssued ? 'success' : 'warning'}
                      />
                    </TableCell>
                    <TableCell>
                      {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '-'}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No tracked signups yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default ReferralAnalytics;
